import React, { useCallback, useRef } from 'react';
import { ReactThreeFiber, useFrame } from 'react-three-fiber';
import { MathUtils, DoubleSide, CylinderBufferGeometry, Mesh, MeshBasicMaterial } from 'three';
import lerp from 'lerp';

type TLoaderProps = {
  position?: ReactThreeFiber.Vector3;
  scale?: ReactThreeFiber.Vector3;
};

const fillAnim = (thetaStart: number, thetaLength: number): [number, number, boolean] => {
  const lengthCriticalValue = 2 * PI;
  thetaStart = PI;
  thetaLength = Math.min(lerp(thetaLength, lengthCriticalValue + 1, 0.008), lengthCriticalValue);
  const isComplete = thetaLength === 2 * PI;

  return [thetaStart, thetaLength, isComplete];
};

const fillOutAnim = (thetaStart: number, thetaLength: number): [number, number, boolean] => {
  const startCriticalValue = 3 * PI;
  thetaStart = Math.min(lerp(thetaStart, startCriticalValue + 1, 0.008), startCriticalValue);
  thetaLength = 3 * PI - thetaStart;
  const isComplete = thetaLength === 0;

  return [thetaStart, thetaLength, isComplete];
};

const PI = Math.PI;
const { degToRad } = MathUtils;

let isFill = true;

const Loader = ({ position, scale }: TLoaderProps) => {
  const cylinder = useRef<Mesh<CylinderBufferGeometry, MeshBasicMaterial>>();
  const handleFrameUpdate = useCallback(() => {
    if (cylinder.current) {
      const { geometry } = cylinder.current;
      let isComplete;
      let { thetaStart, thetaLength } = geometry.parameters;

      if (isFill) {
        [thetaStart, thetaLength, isComplete] = fillAnim(thetaStart, thetaLength);
      } else {
        [thetaStart, thetaLength, isComplete] = fillOutAnim(thetaStart, thetaLength);
      }

      if (isComplete) {
        isFill = !isFill;
      }

      const { radiusTop, radiusBottom, height, radialSegments, heightSegments, openEnded } = geometry.parameters;

      cylinder.current.geometry = new CylinderBufferGeometry(
        radiusTop,
        radiusBottom,
        height,
        radialSegments,
        heightSegments,
        openEnded,
        thetaStart,
        thetaLength
      );
    }
  }, []);

  useFrame(handleFrameUpdate);

  return (
    <group position={position} scale={scale}>
      <mesh rotation={[degToRad(30), 0, 0]} position={[0, 0, 0]} ref={cylinder}>
        <cylinderBufferGeometry attach="geometry" args={[0.15, 0.2, 0.05, 64, 1, true, PI, 0]} />
        <meshBasicMaterial attach="material" side={DoubleSide} color="#ffffff" />
      </mesh>

      <mesh rotation={[degToRad(-60), 0, 0]} position={[0, -0.25, 0]}>
        <circleBufferGeometry attach="geometry" args={[0.05, 32]} />
        <meshBasicMaterial attach="material" side={DoubleSide} color="#ffffff" />
      </mesh>
    </group>
  );
};

export default Loader;
